/**
 * P3 — Brasil state / community / influencer metrics aggregator.
 * Subscribes to the existing `vs:event` bus and tallies events per state /
 * per influencer in-memory. No fake data: counts start at 0 and grow only
 * when real events fire. Exposes a tiny subscribe API for React panels.
 */

type Listener = () => void;

interface StateCounts {
  total: number;
  today: number;
  shares: number;
  predictions: number;
  selects: number;
}

interface InfluencerCounts {
  total: number;
  today: number;
  shares: number;
  referrals: number;
  predictions: number;
  views: number;
}

interface FeedEntry {
  id: string;
  at: number;
  kind: "share" | "join" | "predict" | "rivalry" | "community";
  state?: string;
  message: string;
}

interface ShareItem {
  kind: "palpite" | "ranking" | "estado" | "influencer";
  count: number;
  label: string;
}

const stateMap = new Map<string, StateCounts>();
const stateHistory = new Map<string, number[]>(); // last 7 daily totals
const influencerMap = new Map<string, InfluencerCounts>();
const feed: FeedEntry[] = [];
const shareCounts: Record<ShareItem["kind"], ShareItem> = {
  palpite:    { kind: "palpite",    count: 0, label: "Palpites compartilhados" },
  ranking:    { kind: "ranking",    count: 0, label: "Rankings compartilhados" },
  estado:     { kind: "estado",     count: 0, label: "Estados compartilhados" },
  influencer: { kind: "influencer", count: 0, label: "Influencers compartilhados" },
};

let communityTotal = 0;
let communityToday = 0;
let communityWeek = 0;

const listeners = new Set<Listener>();
let initialized = false;
let lastFeedAt = 0;
const FEED_MIN_GAP_MS = 1500;
const FEED_MAX = 25;

function notify() { listeners.forEach((l) => { try { l(); } catch { /* noop */ } }); }

function bumpState(stateId: string, patch: Partial<StateCounts>) {
  const cur = stateMap.get(stateId) ?? { total: 0, today: 0, shares: 0, predictions: 0, selects: 0 };
  stateMap.set(stateId, {
    total: cur.total + (patch.total ?? 0),
    today: cur.today + (patch.today ?? 0),
    shares: cur.shares + (patch.shares ?? 0),
    predictions: cur.predictions + (patch.predictions ?? 0),
    selects: cur.selects + (patch.selects ?? 0),
  });
}

function bumpInfluencer(slug: string, patch: Partial<InfluencerCounts>) {
  const cur = influencerMap.get(slug) ?? { total: 0, today: 0, shares: 0, referrals: 0, predictions: 0, views: 0 };
  influencerMap.set(slug, {
    total: cur.total + (patch.total ?? 0),
    today: cur.today + (patch.today ?? 0),
    shares: cur.shares + (patch.shares ?? 0),
    referrals: cur.referrals + (patch.referrals ?? 0),
    predictions: cur.predictions + (patch.predictions ?? 0),
    views: cur.views + (patch.views ?? 0),
  });
}

function pushFeed(entry: Omit<FeedEntry, "id" | "at">) {
  const now = Date.now();
  if (now - lastFeedAt < FEED_MIN_GAP_MS) return;
  lastFeedAt = now;
  feed.unshift({ id: `${now}-${Math.random().toString(36).slice(2, 7)}`, at: now, ...entry });
  if (feed.length > FEED_MAX) feed.length = FEED_MAX;
}

function handle(eventType: string, data: Record<string, unknown>) {
  const country = (data.country as string | undefined)?.toUpperCase();
  if (country && country !== "BR") return;
  const state = (data.state as string | undefined)?.toLowerCase();
  const slug = (data.influencerSlug as string | undefined) ?? (data.slug as string | undefined);

  switch (eventType) {
    case "state_team_selected":
      if (state) { bumpState(state, { total: 1, today: 1, selects: 1 }); pushFeed({ kind: "join", state, message: `🇧🇷 Novo torcedor para ${state.toUpperCase()}` }); }
      break;
    case "state_team_share_clicked":
    case "state_team_share":
      if (state) { bumpState(state, { total: 1, today: 1, shares: 1 }); shareCounts.estado.count += 1; pushFeed({ kind: "share", state, message: `🔥 ${state.toUpperCase()} foi compartilhado` }); }
      break;
    case "first_prediction_completed":
      if (state) bumpState(state, { total: 1, today: 1, predictions: 1 });
      pushFeed({ kind: "predict", state, message: "🎯 Mais um palpite enviado pela torcida" });
      break;
    case "brasil_share_template_clicked": {
      const tpl = (data.template as string | undefined) ?? "";
      if (tpl === "ranking") shareCounts.ranking.count += 1;
      else if (tpl === "score" || tpl === "prediction") shareCounts.palpite.count += 1;
      pushFeed({ kind: "share", message: "🇧🇷 Torcida compartilhando no WhatsApp" });
      break;
    }
    case "influencer_page_view":
      if (slug) { bumpInfluencer(slug, { total: 1, today: 1, views: 1 }); }
      break;
    case "influencer_share":
      if (slug) { bumpInfluencer(slug, { total: 1, today: 1, shares: 1 }); shareCounts.influencer.count += 1; }
      break;
    case "influencer_signup":
    case "influencer_referral":
      if (slug) { bumpInfluencer(slug, { total: 1, today: 1, referrals: 1 }); communityTotal += 1; communityToday += 1; communityWeek += 1; }
      break;
    case "influencer_prediction":
      if (slug) bumpInfluencer(slug, { total: 1, today: 1, predictions: 1 });
      break;
  }
  notify();
}

export function initBrasilMetrics(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  window.addEventListener("vs:event", ((e: CustomEvent<{ event_type?: string; event_data?: Record<string, unknown> }>) => {
    const det = e.detail;
    if (!det?.event_type) return;
    handle(det.event_type, det.event_data ?? {});
  }) as EventListener);
}

export function subscribeBrasilMetrics(l: Listener): () => void {
  initBrasilMetrics();
  listeners.add(l);
  return () => listeners.delete(l);
}

export function getStateMetrics(stateId: string): StateCounts {
  return stateMap.get(stateId) ?? { total: 0, today: 0, shares: 0, predictions: 0, selects: 0 };
}

export function getStateRanking(stateIds: string[]): Array<{ id: string; counts: StateCounts; rank: number }> {
  const rows = stateIds.map((id) => ({ id, counts: getStateMetrics(id) }));
  rows.sort((a, b) => b.counts.total - a.counts.total || b.counts.today - a.counts.today);
  return rows.map((r, i) => ({ ...r, rank: i + 1 }));
}

export function getInfluencerMetrics(slug: string): InfluencerCounts {
  return influencerMap.get(slug) ?? { total: 0, today: 0, shares: 0, referrals: 0, predictions: 0, views: 0 };
}

export function getCommunityCounters() {
  return { total: communityTotal, today: communityToday, week: communityWeek };
}

export function getShareLeaderboard(): ShareItem[] {
  return Object.values(shareCounts).sort((a, b) => b.count - a.count);
}

export function getCommunityFeed(): FeedEntry[] {
  return feed.slice(0, 12);
}

export function computeInfluencerPowerScore(slug: string): { score: number; tier: "fire" | "growing" | "warm" } {
  const m = getInfluencerMetrics(slug);
  // Weighted: referrals 40, shares 30, predictions 20, views 10. Capped at 100.
  const raw = m.referrals * 4 + m.shares * 3 + m.predictions * 2 + m.views * 1;
  const score = Math.min(100, raw);
  const tier = score >= 70 ? "fire" : score >= 30 ? "growing" : "warm";
  return { score, tier };
}

export function computeBrasilDominationScore(): { score: number; status: "green" | "yellow" | "red"; pillars: Array<{ key: string; label: string; value: number }>; } {
  const stateTotal = Array.from(stateMap.values()).reduce((s, c) => s + c.total, 0);
  const shareTotal = Object.values(shareCounts).reduce((s, c) => s + c.count, 0);
  const infTotal = Array.from(influencerMap.values()).reduce((s, c) => s + c.total, 0);
  const usersFactor      = Math.min(20, communityTotal * 2);
  const retentionFactor  = Math.min(15, communityWeek);
  const viralityFactor   = Math.min(20, shareTotal * 2);
  const influencerFactor = Math.min(15, infTotal);
  const torcidasFactor   = Math.min(20, stateTotal);
  const sharedFactor     = Math.min(10, shareTotal);
  const score = usersFactor + retentionFactor + viralityFactor + influencerFactor + torcidasFactor + sharedFactor;
  const status: "green" | "yellow" | "red" = score >= 60 ? "green" : score >= 25 ? "yellow" : "red";
  return {
    score,
    status,
    pillars: [
      { key: "users",       label: "Usuários",   value: usersFactor },
      { key: "retention",   label: "Retenção",   value: retentionFactor },
      { key: "virality",    label: "Viralidade", value: viralityFactor },
      { key: "influencers", label: "Influencers",value: influencerFactor },
      { key: "torcidas",    label: "Torcidas",   value: torcidasFactor },
      { key: "shares",      label: "Compart.",   value: sharedFactor },
    ],
  };
}

// Reserved for future weekly persistence — currently in-session only.
void stateHistory;